// Copyright (c) Microsoft Corporation.
// Licensed under the MIT License.
import {
    Field,
    Link,
    makeStyles,
    MessageBar,
    MessageBarBody,
    ProgressBar,
    shorthands,
    Subtitle2Stronger,
    Text,
    Toast,
    ToastBody,
    Toaster,
    ToastTitle,
    ToastTrigger,
    tokens,
    useId,
    useToastController,
} from "@fluentui/react-components";
import { CacheService } from "@microsoft/mgt";
import { Providers, ProviderState } from "@microsoft/mgt-element";
import { TeamsFxProvider } from "@microsoft/mgt-teamsfx-provider";
import {
    BatchRequestContent,
    BatchRequestStep,
    BatchResponseBody,
    BatchResponseContent,
} from "@microsoft/microsoft-graph-client";
import { DriveItem } from "@microsoft/microsoft-graph-types";
import { app, authentication } from "@microsoft/teams-js";
import {
    TeamsUserCredential,
    TeamsUserCredentialAuthConfig,
} from "@microsoft/teamsfx";
import { AxiosError, AxiosResponse, isAxiosError } from "axios";
import { useCallback, useEffect, useState } from "react";
import {
    actionDekaAction,
    actionDekaActionEntity,
    actionDekaEntity,
    actionDekaKeysegment,
    actionlabelDocument,
    actionlabelPseudo,
    actionlabelTeamsDocumentId,
    actionPseudo,
    actionPseudoAnonymized,
    actionRawJson,
    DYNIZER_TENANT_HEADER_KEY,
} from "../models/const";
import { http } from "../models/http";
import {
    actionLabelTeamGroupId,
    actionLabelUserId,
    allowedFileTypes,
    apiPipelinesToPipelines,
    buildInstanceElements,
    getGroupIdFromContext,
    isRunningLikeStatus,
    mimeTypeToDynizerType,
    pipeline,
    status,
} from "../models/pipeline";
import { checkTenant } from "../services/dynizer/dynizer";
import {
    DQLReq,
    Api as DynizerApi,
    SimpleQueryRes,
} from "../services/openapi/dynizer-axios/Api";
import {
    CreateDocument,
    CreatePipeline,
    Document,
    GetPipelines,
    Api as PipelineApi,
    PrivateDocument,
} from "../services/openapi/pipeline-api-axios/Api";
import "./App.css";
import { errorIcon } from "./FileCustom";
import FileList, { hasValidMimeType } from "./FileList";
import Header from "./Header";
import Loading from "./Loading";
import MicrosoftLogin from "./MicrosoftLogin";
import Polling from "./Polling";
import { Progress } from "./Progress";
import { deleteStatus, ProgressDelete } from "./ProgressDelete";
import { siteView } from "./Settings";
import Summary from "./Summary";
import TenantLogin from "./TenantLogin";
import Tiles from "./Tiles";

const pollingIntervalMs = 3000;

const localConnectionId = "00000000-0000-0000-0000-000000000001";
const lobPath = "blob/";

const Tab = () => {
    const [showLoginPage, setShowLoginPage] = useState(true);
    const [currentFolder, setCurrentFolder] = useState<DriveItem[]>([]);
    const [view, setView] = useState(siteView);
    const [pipelines, setPipelines] = useState<pipeline[]>([]);
    const [json, setJson] = useState("");
    const [pseudoJson, setPseudoJson] = useState("");
    const [context, setContext] = useState<app.Context | undefined>(undefined);
    const [tenant, setTenant] = useState("");
    const [apisHaveTenant, setApisHaveTenant] = useState(false);
    const [reloadItems, setReloadItems] = useState(false);
    const [loading, setLoading] = useState(false);
    const [filterActive, setFilterActive] = useState(true);
    const [selectedFile, setSelectedFile] = useState<DriveItem | undefined>(
        undefined
    );
    const [pipelineApi, setPipelineApi] = useState<PipelineApi<unknown>>(
        new PipelineApi({})
    );
    const [dynizerApi, setDynizerApi] = useState<DynizerApi<unknown>>(
        new DynizerApi({})
    );
    const [loadingJson, setLoadingJson] = useState(false);
    const [loadingPseudoJson, setLoadingPseudoJson] = useState(false);
    const [uploading, setUploading] = useState(false);
    const [uploadedCount, setUploadedCount] = useState(0);
    const [uploadingTotal, setUploadingTotal] = useState(0);
    const [deleteStatus, setDeleteStatus] = useState<deleteStatus>("success");
    const [nbDeleted, setNbDeleted] = useState(0);
    const [nbToDelete, setNbToDelete] = useState(0);

    const styles = useStyles();

    useEffect(() => {
        const init = async () => {
            CacheService?.clearCaches();

            /* 
            Specifies which permissions the app needs during the sign-in process. 
            
            If the permissions have already been consented to by an admin in Microsoft Entra ID (formerly AAD), 
            users won't be prompted again and graph requests will work even if the scopes are not in this array.
            */
            let scopes = [
                "Files.ReadWrite.All",
                "Channel.ReadBasic.All",
                "Sites.ReadWrite.All",
            ];

            const authConfig: TeamsUserCredentialAuthConfig = {
                clientId: process.env.REACT_APP_CLIENT_ID || "",
                initiateLoginEndpoint:
                    process.env.REACT_APP_START_LOGIN_PAGE_URL || "",
            };

            const teamsUserCredential = new TeamsUserCredential(authConfig);

            Providers.globalProvider = new TeamsFxProvider(
                teamsUserCredential,
                scopes
            );

            Providers.globalProvider.onStateChanged((e) => {
                if (Providers.globalProvider.state === ProviderState.SignedOut)
                    setShowLoginPage(true);
            });

            let consentNeeded = false;

            try {
                /* 
                    Access token used in requests to Microsoft Graph, e.g. fetching Teams files and channels
                    The "appid" field in this token corresponds to the "clientId" in TeamsUserCredentialAuthConfig
                 */
                let teamsFxToken =
                    await Providers.globalProvider.getAccessToken();

                if (!teamsFxToken) {
                    consentNeeded = true;
                } else {
                    /* 
                        The SSO token used in requests to dynizer, pipeline-api, rag-api...
                        The "aud" (audience) field in this token correponds to AAD_APP_CLIENT_ID in the manifest.json file
                    */
                    let ssoToken = await authentication.getAuthToken();
                    // console.log("sso token: ", ssoToken);
                }
            } catch (error) {
                consentNeeded = true;
            }

            Providers.globalProvider.setState(
                consentNeeded ? ProviderState.SignedOut : ProviderState.SignedIn
            );

            let context = await app.getContext();
            setContext(context);

            let tenant = getTenantFromLocalStorage();

            if (tenant) {
                let valid = await checkTenant(tenant);
                if (valid) setTenantTrimmed(tenant);
            }

            setShowLoginPage(consentNeeded);
        };

        init();
    }, []);

    useEffect(() => {
        if (!tenant) return;

        setPipelineApi(
            new PipelineApi({
                baseURL: "/pipeline-api",
                headers: {
                    "Content-Type": "application/json;charset=UTF-8",
                    [DYNIZER_TENANT_HEADER_KEY]: tenant,
                },
            })
        );

        setDynizerApi(
            new DynizerApi({
                baseURL: "/dynizer-api",
                headers: {
                    "Content-Type": "application/json;charset=UTF-8",
                    [DYNIZER_TENANT_HEADER_KEY]: tenant,
                },
            })
        );

        saveTenantToLocalStorage(tenant);

        setReloadItems(!reloadItems);

        setApisHaveTenant(true);
    }, [tenant]);

    useEffect(() => {
        setSelectedFile(undefined);
    }, [currentFolder]);

    useEffect(() => {
        if (selectedFile) {
            updatePipelineStatus(selectedFile.id);
            fetchJson(selectedFile.id);
            fetchPseudoJson(selectedFile.id);
        }
    }, [selectedFile]);

    useEffect(() => {
        toRootFolder();
    }, [view]);

    const setTenantTrimmed = (input: string) => {
        setTenant(tenantNameTrimmed(input));
    };

    const updatePipelineStatus = useCallback(
        async (itemId: string | undefined) => {
            if (!itemId) return;

            const pipeline = pipelines.find((p) => p.teamsId === itemId);

            if (!pipeline || !isRunningLikeStatus(pipeline.status)) return;

            try {
                const res = await getPipelineById(pipeline.pipelineId);
                if (res.status !== http.OK) {
                    catchAxiosWrong2XX(res);
                    return;
                }

                const gotStatus = res.data.info.status;

                if (!isRunningLikeStatus(gotStatus)) {
                    if (gotStatus === status.SUCCESS) {
                        await fetchAndUpdateSuccessPipeline(itemId);
                        fetchJson(itemId);
                        fetchPseudoJson(itemId);
                    } else {
                        setPipelineStatus(pipeline.pipelineId, gotStatus);
                    }
                }
            } catch (err: any) {
                catchAxiosNon2XX(err);
            }
        },
        [selectedFile, pipelines]
    );

    const setPipelineStatus = (pipelineId: string, inStatus: string) => {
        setPipelines(
            pipelines.map((pipeline) => {
                return pipeline.pipelineId === pipelineId
                    ? {
                          ...pipeline,
                          status: inStatus,
                      }
                    : pipeline;
            })
        );
    };

    const fileClick = (file: DriveItem) => {
        if (loading) return;

        if (file.folder && file.name) {
            let index = currentFolder.findIndex((item) => item.id === file.id);

            if (currentFolder.length && index === currentFolder.length - 1)
                return;

            setCurrentFolder((prevArray) =>
                index === -1
                    ? [...prevArray, file]
                    : prevArray.slice(0, index + 1)
            );

            return;
        }

        setLoadingJson(true);
        setLoadingPseudoJson(true);
        setSelectedFile(file.id === selectedFile?.id ? undefined : file);
    };

    const toRootFolder = () => {
        setCurrentFolder([]);
    };

    const getPipelineById = async (id: string) => {
        let dynizerJwtToken = await authentication.getAuthToken();
        return pipelineApi.pipelines.getPipelineById(
            id,
            {},
            {
                headers: {
                    Authorization: `Bearer ${dynizerJwtToken}`,
                },
            }
        );
    };

    const fetchJson = async (itemId: string | undefined) => {
        setLoadingJson(true);

        try {
            const pipeline = pipelines.find(
                (p) => p.teamsId === itemId && p.status === status.SUCCESS
            );

            if (!pipeline || !pipeline.clobId) return;

            let [lobType, uuid] = pipeline.clobId.split("/");

            let result = await downloadObject(lobType, uuid, tenant);

            setJson(result);
        } catch (err: any) {
            setError(err);
        } finally {
            setLoadingJson(false);
        }
    };

    const fetchAndUpdateSuccessPipeline = async (itemId: string) => {
        let gotPipelines = await fetchSuccessPipelines([itemId]);

        if (gotPipelines.length === 0) {
            return;
        }

        let newPipelines = pipelines;

        let gotPipeline = gotPipelines[0];

        let index = newPipelines.findIndex(
            (pipeline) => pipeline.teamsId === gotPipeline.teamsId
        );

        if (index === -1) {
            newPipelines = [...newPipelines, gotPipeline];
        } else {
            newPipelines[index] = gotPipeline;
        }

        setPipelines(newPipelines);
    };

    const fetchAndSetPipelines = async (
        items: DriveItem[],
        append: boolean
    ) => {
        let gotPipelines = await fetchPipelines(items);

        setPipelines(append ? [...pipelines, ...gotPipelines] : gotPipelines);
    };

    const fetchPipelines = async (items: DriveItem[]) => {
        let itemIds = items
            .filter((item) => hasValidMimeType(item))
            .map((item) => item.id || "")
            .filter((id) => !!id);

        let [successPipelines, pipelineApiPipelines] = await Promise.all([
            fetchSuccessPipelines(itemIds),
            fetchNonSuccessPipelines(itemIds),
        ]);

        return joinPipelines(successPipelines, pipelineApiPipelines);
    };

    const joinPipelines = (
        successPipelines: pipeline[],
        gotPipelines: pipeline[]
    ) => {
        gotPipelines.forEach((gotPipeline) => {
            if (gotPipeline.status === status.SUCCESS) {
                return;
            }

            let index = successPipelines.findIndex((successPipeline) => {
                successPipeline.teamsId === gotPipeline.teamsId;
            });

            if (index != -1) {
                successPipelines[index] = gotPipeline;
            } else {
                successPipelines = [...successPipelines, gotPipeline];
            }
        });

        return successPipelines;
    };

    const fetchPseudoJson = async (itemId: string | undefined) => {
        setLoadingPseudoJson(true);

        try {
            let pipeline = pipelines.find(
                (p) => p.teamsId === itemId && p.status === status.SUCCESS
            );

            if (!pipeline) return;

            let dqlReq: DQLReq = {
                dql: `
            SELECT ${actionlabelPseudo}, pipeline_id, ${actionLabelUserId}, ${actionLabelTeamGroupId} 
            FROM ${actionPseudo}
            WHERE pipeline_id = '${pipeline.pipelineId}'`,
            };

            let dynizerJwtToken = await authentication.getAuthToken();

            let params = {
                headers: {
                    Authorization: `Bearer ${dynizerJwtToken}`,
                },
            };

            let res: AxiosResponse<SimpleQueryRes, any>;

            try {
                res = await dynizerApi.api.simpleQuery(dqlReq, params);
                if (res.status !== http.OK) return catchAxiosWrong2XX(res);
            } catch (err: any) {
                catchAxiosNon2XX(err);
                return;
            }

            const selectRes = res.data.result[0]?.select_result;
            const clobPath = selectRes?.rows?.[0]?.instance_cell_values?.[0];
            if (!clobPath) return setPseudoJson("");

            let [lobType, refUuid] = clobPath.split("/");

            try {
                const downloadRes = await downloadObject(
                    lobType,
                    refUuid,
                    tenant
                );
                setPseudoJson(downloadRes);
            } catch (e: any) {
                setError(e);
            }
        } finally {
            setLoadingPseudoJson(false);
        }
    };

    const fetchSuccessPipelines = async (
        itemIds: string[]
    ): Promise<pipeline[]> => {
        if (itemIds.length === 0) return [];

        let dqlReq: DQLReq = {
            dql: `
            SELECT  ${actionlabelTeamsDocumentId}, ${actionlabelDocument}, pipeline_id, ${actionLabelUserId}, ${actionLabelTeamGroupId} 
            FROM ${actionRawJson} 
            WHERE ${actionlabelTeamsDocumentId} IN (${dqlIn(itemIds)})
            `,
        };

        let dynizerJwtToken = await authentication.getAuthToken();
        let params = {
            headers: {
                Authorization: `Bearer ${dynizerJwtToken}`,
            },
        };

        let res: AxiosResponse<SimpleQueryRes, any>;
        try {
            res = await dynizerApi.api.simpleQuery(dqlReq, params);
            if (res.status !== http.OK) {
                catchAxiosWrong2XX(res);
                throw new Error(`Unexpected status code: ${res.status}`);
            }
        } catch (err: any) {
            const msg = err?.response?.data?.message ?? "";

            const ignoreMessages = [
                "ActionDataNoActionWithTypeName",
                "ActionDataNoActionWithSchemaName",
            ];

            if (ignoreMessages.some((substr) => msg.includes(substr))) {
                return [];
            }
            catchAxiosNon2XX(err);
            throw err;
        }

        const selectRes = res.data.result[0]?.select_result;

        return (
            selectRes?.rows.map((row) => ({
                teamsId: row.instance_cell_values?.[0] || "",
                clobId: row.instance_cell_values?.[1] || "",
                pipelineId: row.instance_cell_values?.[2] || "",
                status: status.SUCCESS,
            })) || []
        );
    };

    const fetchNonSuccessPipelines = async (itemIds: string[]) => {
        if (itemIds.length === 0) return [];

        const dynizerJwtToken = await authentication.getAuthToken();
        const params = {
            headers: { Authorization: `Bearer ${dynizerJwtToken}` },
        };

        let res: AxiosResponse<GetPipelines, any>;
        try {
            res = await pipelineApi.pipelines.getFilteredPipelines(
                { client_ids: itemIds },
                {},
                params
            );
        } catch (err: any) {
            catchAxiosNon2XX(err);
            throw err;
        }

        if (res.status !== http.OK) {
            catchAxiosWrong2XX(res);
            throw new Error(`Unexpected status code: ${res.status}`);
        }

        const gotPipelines = res.data.records;
        if (!gotPipelines) {
            const errorMessage = `Fetching pipelines failed with status code ${res.status}: records undefined`;
            setError(errorMessage);
            throw new Error(errorMessage);
        }

        return apiPipelinesToPipelines(
            gotPipelines,
            actionlabelTeamsDocumentId
        );
    };

    const batchUpload = async (files: DriveItem[]) => {
        setUploadedCount(0);
        setUploadingTotal(files.length);
        setUploading(true);

        for (const file of files) {
            const url = (file as any)["@microsoft.graph.downloadUrl"];
            const blob = await downloadFile(url);

            if (blob) {
                await createStorageAndDocumentAndPipeline(blob, file);
            }

            setUploadedCount((count) => count + 1);
        }

        setUploading(false);
    };

    const downloadFile = (url: string): Promise<Blob | null> => {
        return new Promise((resolve, reject) => {
            const xhttp = new XMLHttpRequest();
            xhttp.responseType = "blob";
            xhttp.open("GET", url, true);

            xhttp.onreadystatechange = () => {
                if (xhttp.readyState === XMLHttpRequest.DONE) {
                    if (xhttp.status === http.OK) {
                        resolve(xhttp.response);
                    } else {
                        catchXhttpNon2XX(xhttp, url);
                        resolve(null);
                    }
                }
            };

            xhttp.send();
        });
    };

    const createStorageAndDocumentAndPipeline = async (
        blob: any,
        file: DriveItem
    ) => {
        const method = "POST";
        const url = `/pipeline-api/connections/${localConnectionId}/storage?path=${lobPath}`;
        const dynizerJwtToken = await authentication.getAuthToken();

        const storageResponse = await new Promise<string | null>(
            (resolve, reject) => {
                const xhttp = new XMLHttpRequest();
                xhttp.open(method, url, true);
                setXhttpHeaders(xhttp, dynizerJwtToken, tenant);

                xhttp.onreadystatechange = () => {
                    if (xhttp.readyState === XMLHttpRequest.DONE) {
                        if (xhttp.status === http.OK) {
                            const res = JSON.parse(xhttp.responseText);
                            resolve(res.path);
                        } else {
                            catchXhttpNon2XX(xhttp, url);
                            resolve(null);
                        }
                    }
                };

                xhttp.send(blob);
            }
        );

        if (storageResponse) {
            await createDocumentAndPipeline(storageResponse, file);
        }
    };

    const createDocumentAndPipeline = async (path: string, file: DriveItem) => {
        let dynizerType: string | undefined;
        let teamsId = file.id;

        if (!file?.file?.mimeType || !teamsId) {
            setError(`File has no mimetype or has no id`);
            return;
        }

        dynizerType = mimeTypeToDynizerType[file.file?.mimeType];

        if (!dynizerType) {
            setError(
                `Cannot analyze this file type, can only analyze types: ${allowedFileTypes()}`
            );
            return;
        }

        let privateDocument: PrivateDocument = {
            connection_id: localConnectionId,
            path: path,
        };

        let document: Document = {
            filetype_configuration: { name: dynizerType },
            private: privateDocument,
        };

        const createDocument: CreateDocument = {
            document: document,
            name: teamsId,
        };

        const dynizerJwtToken = await authentication.getAuthToken();

        try {
            const res = await pipelineApi.documents.postDocuments(
                createDocument,
                {
                    headers: {
                        Authorization: `Bearer ${dynizerJwtToken}`,
                    },
                }
            );

            if (res.status === http.OK) {
                await createPipeline(res.data.id, teamsId, file);
            } else {
                catchAxiosWrong2XX(res);
            }
        } catch (err: any) {
            catchAxiosNon2XX(err);
        }
    };

    const createPipeline = async (
        documentId: string,
        fileId: string,
        file: DriveItem
    ) => {
        const dynizerJwtToken = await authentication.getAuthToken();

        const createPipelineReq: CreatePipeline = {
            document_id: documentId,
            configuration: {
                plugins: [
                    {
                        name: "raw",
                        config: {
                            action: actionRawJson,
                            unique_labels: [actionlabelTeamsDocumentId],
                            data_type: "DT_Document",
                        },
                    },
                    {
                        name: "deka (Dashboard Entities, Keysegments and Actions)",
                        config: {
                            action_entities: actionDekaEntity,
                            action_actions: actionDekaAction,
                            action_keysegments: actionDekaKeysegment,
                            action_action_entity_link: actionDekaActionEntity,
                        },
                    },
                    {
                        name: "pseudonymize",
                        config: {
                            component_types: [],
                            entity_types: [
                                "DATE",
                                "ORG",
                                "PERSON",
                                "LOC",
                                "FAC",
                                "GPE",
                                "NORP",
                            ],
                            action: actionPseudo,
                            action_anonymized: actionPseudoAnonymized,
                            actionlabel: actionlabelPseudo,
                        },
                    },
                ],
                name: "Microsoft Teams App",
                instance: {
                    instance_elements: buildInstanceElements(
                        actionlabelTeamsDocumentId,
                        fileId,
                        file,
                        context
                    ),
                },
                client_id: fileId,
                connection_id: localConnectionId,
                flow_configuration: flowConfiguration,
            },
        };

        try {
            const res = await pipelineApi.pipelines.postPipelines(
                createPipelineReq,
                {
                    headers: {
                        Authorization: `Bearer ${dynizerJwtToken}`,
                    },
                }
            );

            if (res.status === http.OK) {
                const newPipeline: pipeline = {
                    status: res.data.info.status,
                    teamsId: fileId,
                    pipelineId: res.data.id,
                };

                setPipelines((prevPipelines) => [
                    ...prevPipelines,
                    newPipeline,
                ]);
            } else {
                catchAxiosWrong2XX(res);
            }
        } catch (err: any) {
            catchAxiosNon2XX(err);
        }
    };

    const deleteItems = async (
        itemsDeka: DriveItem[],
        itemsSP: DriveItem[],
        deleteSp: boolean
    ) => {
        setDeleteStatus("in progress");
        setNbDeleted(0);
        setNbToDelete(
            deleteSp ? itemsDeka.length + itemsSP.length : itemsDeka.length
        );

        const handleErr = (error: unknown) => {
            setError(
                "Error during deletion: " +
                    (error instanceof Error ? error.message : String(error))
            );
            setDeleteStatus("failed");
        };

        // Step 1: Initialize session
        const dynizerJwt = await authentication.getAuthToken();
        try {
            await dynizerApi.api.simpleQuery(
                { dql: `SET query_v3_session = true` },
                { headers: { Authorization: `Bearer ${dynizerJwt}` } }
            );
        } catch (error) {
            handleErr(error);
            return;
        }

        // Step 2: Delete files that have plugin data
        let batchSizeDelete = 1;
        for (let i = 0; i < itemsDeka.length; i += batchSizeDelete) {
            let batch = itemsDeka.slice(i, i + batchSizeDelete);

            // Step 2.1: Delete all plugin data
            let ids = batch.map((item) => `'${item.id}'`).join(",");
            let whereClause = `WHERE ${actionlabelTeamsDocumentId} IN (${ids})`;

            try {
                await deletePluginData(whereClause, dynizerJwt);
            } catch (error) {
                handleErr(error);
                return;
            }

            // Step 2.2: Delete SharePoint files after plugin data is deleted
            if (deleteSp) {
                try {
                    await deleteSPItems(batch);
                } catch (error) {
                    handleErr(error);
                    return;
                }
            }

            setNbDeleted((count) => count + batch.length);
        }

        if (!deleteSp || itemsSP.length === 0) {
            setDeleteStatus("success");
            setReloadItems(!reloadItems);
            return;
        }

        // Step 3: Delete files without plugin data
        try {
            await deleteSPItems(itemsSP);
        } catch (error) {
            handleErr(error);
            return;
        }

        setNbDeleted((count) => count + itemsSP.length);
        setDeleteStatus("success");
        setReloadItems(!reloadItems);
    };

    const deletePluginData = async (
        whereClause: string,
        dynizerJwtToken: string
    ) => {
        const actions = [
            actionDekaEntity,
            actionDekaKeysegment,
            actionDekaAction,
            actionDekaActionEntity,
            actionPseudo,
            actionPseudoAnonymized,
        ];

        // DELETE for first 6 actions, raw_json should be last to make sure UI files are not analyzable when delete in progress
        const deletePromises = actions.map((action) => {
            const dql = `DELETE FROM ${action} ${whereClause}`;
            return dynizerApi.api.simpleQuery(
                { dql },
                { headers: { Authorization: `Bearer ${dynizerJwtToken}` } }
            );
        });

        await Promise.all(deletePromises);

        // DELETE for raw_json
        const dql = `DELETE FROM ${actionRawJson} ${whereClause}`;
        await dynizerApi.api.simpleQuery(
            { dql },
            { headers: { Authorization: `Bearer ${dynizerJwtToken}` } }
        );
    };

    const deleteSPItems = async (items: DriveItem[]) => {
        const batchSizeSharepoint = 20; // Max requests per graph batch
        const deleteFilesPromises = [];

        for (let j = 0; j < items.length; j += batchSizeSharepoint) {
            const batchPayload = await new BatchRequestContent(
                items.slice(j, j + batchSizeSharepoint).map(
                    (file): BatchRequestStep => ({
                        id: file.id || "", // Unique identifier for the batch request
                        request: new Request(
                            `/drives/${file.parentReference?.driveId}/items/${file.id}`,
                            { method: "DELETE" }
                        ),
                    })
                )
            ).getContent();

            // Add the batch request to the promises array
            deleteFilesPromises.push(
                Providers.globalProvider.graph.client
                    .api("/$batch")
                    .post(batchPayload)
                    .then((res: BatchResponseBody) => {
                        const batchResponse = new BatchResponseContent(res);
                        batchResponse.getResponses().forEach((response, id) => {
                            if (response.status !== 204) {
                                console.error(
                                    `Error deleting file ${id}: ${response.body}`
                                );
                            }
                        });
                    })
            );
        }

        return Promise.all(deleteFilesPromises);
    };

    const openDriveItem = async (itemID: string) => {
        let groupId = getGroupIdFromContext(context);

        const url = groupId
            ? `/groups/${groupId}/drive/items/${itemID}`
            : `me/drive/items/${itemID}`;

        const driveItem = await Providers.globalProvider.graph.client
            .api(url)
            .get();

        openFileInNewTab(driveItem);
    };

    const loginBtnClick = async () => {
        try {
            if (!Providers.globalProvider.login) {
                throw new Error("Provider has no login method");
            }

            await Providers.globalProvider.login();

            Providers.globalProvider.setState(ProviderState.SignedIn);

            setShowLoginPage(false);

            let ssoToken = await authentication.getAuthToken();
            // console.log("sso token: ", ssoToken);
        } catch (err: any) {
            if (err.message?.includes("CancelledByUser")) {
                const helpLink = "https://aka.ms/teamsfx-auth-code-flow";
                err.message +=
                    '\nIf you see "AADSTS50011: The reply URL specified in the request does not match the reply URLs configured for the application" ' +
                    "in the popup window, you may be using unmatched version for TeamsFx SDK (version >= 0.5.0) and Teams Toolkit (version < 3.3.0) or " +
                    `cli (version < 0.11.0). Please refer to the help link for how to fix the issue: ${helpLink}`;
            }
            alert("Login failed: " + err);
            return;
        }
    };

    const toasterId = useId("toaster");
    const toasterIdProgress = useId("progress");

    const { dispatchToast } = useToastController(toasterId);

    const setError = (msg: unknown) => {
        let errorMessage: string;

        if (msg instanceof Error) {
            errorMessage = msg.message;
        } else if (typeof msg === "string") {
            errorMessage = msg;
        } else if (msg && typeof msg === "object" && "message" in msg) {
            errorMessage = String((msg as any).message);
        } else {
            errorMessage = String(msg);
        }
        dispatchToast(jsxToast("Error", errorMessage), { intent: "error" });
    };

    const setSuccess = (title: string, msg: string) =>
        dispatchToast(jsxToast(title, msg), { intent: "success" });

    const jsxToast = (title: string, msg: string) => (
        <Toast>
            <ToastTitle
                action={
                    <ToastTrigger>
                        <Link>Dismiss</Link>
                    </ToastTrigger>
                }
            >
                {title}
            </ToastTitle>
            {msg != "" && <ToastBody>{msg}</ToastBody>}
        </Toast>
    );

    const catchAxiosWrong2XX = (res: AxiosResponse) => {
        setError(`HTTP status ${res.status}: ${res.statusText}`);
    };

    const catchAxiosNon2XX = (err: Error | AxiosError) => {
        if (isAxiosError(err)) {
            if (err.response?.status === 401) {
                setTenant("");
                setApisHaveTenant(false);
            }

            if (err.response) {
                setError(
                    `Request to ${err.response.config.url} failed with status ${err.response.status}, ${err.response.statusText}: ${err.response.data.message}`
                );
                return;
            }

            if (err.request) {
                setError(`Request to ${err.config?.url} received no response`);
                return;
            }
        }

        setError(`Error in setting up request to server: ${err.message}`);
    };

    const catchXhttpNon2XX = (xhttp: XMLHttpRequest, url: string) => {
        let out = buildXhttpError(xhttp, url);

        setError(out);
    };

    const jsxResults = () => {
        if (!selectedFile) return;

        let pipeline = pipelines.find(
            (pipeline) => pipeline.teamsId === selectedFile.id
        );

        if (!pipeline) return;

        if (isRunningLikeStatus(pipeline.status)) {
            return (
                <Field
                    validationMessage={
                        <>
                            Analyzing Document <br />
                            (Pipeline ID: {pipeline?.pipelineId})
                        </>
                    }
                    validationState="none"
                >
                    <ProgressBar />
                </Field>
            );
        } else if (
            pipeline.status === status.FAILED ||
            pipeline.status === status.CANCELED
        ) {
            return jsxErrorMessage(
                `Analysis status: ${pipeline.status}\n(Pipeline ID: ${pipeline.pipelineId})`
            );
        } else if (pipeline.status === status.SUCCESS) {
            if (pipeline.clobId) {
                return (
                    <>
                        <Tiles
                            inJson={json}
                            inPseudoJson={pseudoJson}
                            file={selectedFile}
                            setError={setError}
                            setSuccess={setSuccess}
                            jsonLoading={loadingJson}
                            pseudoJsonLoading={loadingPseudoJson}
                        ></Tiles>
                        <Subtitle2Stronger
                            style={{
                                margin: "20px 0px",
                            }}
                        >
                            Summary
                        </Subtitle2Stronger>
                        {loadingJson ? (
                            <div style={{ height: "200px" }}>
                                <Loading />
                            </div>
                        ) : (
                            <div className={styles.summaryWrapper}>
                                <Summary inJson={json}></Summary>
                            </div>
                        )}
                    </>
                );
            } else {
                return jsxErrorMessage(`Analysis result not found`);
            }
        }
    };

    const jsxErrorMessage = (message: string) => (
        <MessageBar intent="error" icon={errorIcon}>
            <MessageBarBody className="pre-wrap">{message}</MessageBarBody>
        </MessageBar>
    );

    const jsxSelectedDocument = selectedFile ? (
        <Text
            size={500}
            weight="bold"
            className="underline-on-hover"
            role="button"
            onClick={() => {
                openFileInNewTab(selectedFile);
            }}
        >
            {" " + (selectedFile?.name || "")}
        </Text>
    ) : (
        <MessageBar>
            <MessageBarBody>No Document selected</MessageBarBody>
        </MessageBar>
    );

    return (
        <div>
            <Toaster
                toasterId={toasterId}
                position="top-end"
                limit={3}
                timeout={7000}
                pauseOnWindowBlur={true}
                pauseOnHover={true}
            />
            <Toaster toasterId={toasterIdProgress} />

            <Progress
                toasterId={toasterIdProgress}
                value={uploadedCount}
                max={uploadingTotal}
                uploading={uploading}
            ></Progress>

            <ProgressDelete
                toasterId={toasterIdProgress}
                value={nbDeleted}
                max={nbToDelete}
                deleting={deleteStatus}
            ></ProgressDelete>

            {showLoginPage ? (
                <MicrosoftLogin loginBtnClick={loginBtnClick}></MicrosoftLogin>
            ) : (
                <div className={styles.paddingTopBottom}>
                    {!apisHaveTenant ? (
                        <div className="main-container" style={{ padding: 10 }}>
                            <TenantLogin
                                setTenant={setTenantTrimmed}
                            ></TenantLogin>
                        </div>
                    ) : (
                        <div className="main-container flex-column">
                            <div className={styles.header}>
                                <Header
                                    context={context}
                                    setTenant={setTenantTrimmed}
                                    tenant={tenant}
                                    setView={setView}
                                    view={view}
                                    loading={loading}
                                    filterActive={filterActive}
                                    setFilterActive={setFilterActive}
                                ></Header>
                            </div>
                            <div className="contents">
                                <div className="mgt-col">
                                    <FileList
                                        uploading={uploading}
                                        deleteStatus={deleteStatus}
                                        deleteSelection={deleteItems}
                                        reload={reloadItems}
                                        context={context}
                                        selectedFile={selectedFile}
                                        currentFolder={currentFolder}
                                        view={view}
                                        toRootFolder={toRootFolder}
                                        pipelines={pipelines}
                                        setError={setError}
                                        fileClick={fileClick}
                                        startAnalysis={batchUpload}
                                        tenant={tenant}
                                        loading={loading}
                                        setLoading={setLoading}
                                        filterActive={filterActive}
                                        catchXhttpNon2XX={catchXhttpNon2XX}
                                        fetchPipelines={fetchPipelines}
                                        fetchAndSetPipelines={
                                            fetchAndSetPipelines
                                        }
                                        fetchSuccessPipelines={
                                            fetchSuccessPipelines
                                        }
                                        fetchNonSuccessPipelines={
                                            fetchNonSuccessPipelines
                                        }
                                        openDriveItem={openDriveItem}
                                    ></FileList>
                                </div>

                                <div
                                    className="mgt-col"
                                    style={{ paddingRight: "20px" }}
                                >
                                    <div
                                        className="flex-column"
                                        style={{
                                            height: "100%",
                                        }}
                                    >
                                        {jsxSelectedDocument}
                                        <br />
                                        {jsxResults()}
                                    </div>
                                    <Polling
                                        pollHook={() =>
                                            updatePipelineStatus(
                                                selectedFile?.id
                                            )
                                        }
                                        intervalMs={pollingIntervalMs}
                                    ></Polling>
                                </div>
                            </div>
                        </div>
                    )}
                </div>
            )}
        </div>
    );
};

export default Tab;

export const saveTenantToLocalStorage = (tenant: string) => {
    localStorage.setItem("tenant", tenant);
};

export const getTenantFromLocalStorage = () => {
    return localStorage.getItem("tenant");
};

const dqlIn = (inValues: string[]) => {
    let out = "";

    if (inValues.length === 0) {
        return out;
    }

    for (let value of inValues) {
        out += `'${value}',`;
    }

    return out.substring(0, out.length - 1);
};

const flowConfiguration = {
    config: {
        analyze: {
            merge_entities: true,
            tokenizer: "stanford",
        },
        keysegments: {
            keysegments_type: "default",
            labels: [],
            lower_keysegment_text: true,
            max_keywords: 50,
        },
        language: {
            target: ["de", "en", "es", "fr", "it", "nl", "pt"],
        },
        summary: {
            generate_text_summary: true,
            labels: [],
            method: "default",
            min_sentences: 1,
            percentage: 25,
        },
    },
    name: "analyze",
};

export const tenantNameTrimmed = (name: string) => name.trim().toLowerCase();

export const downloadObject = async (
    type: string,
    uuid: any,
    tenant: string
): Promise<string> => {
    return new Promise(async (resolve, reject) => {
        let xhttp = new XMLHttpRequest();
        let url = `/dynizer-object-api/api/v2/objects/download/${type}/${uuid}`;
        let dynizerJwtToken = await authentication.getAuthToken();

        xhttp.open("GET", url, true);
        setXhttpHeaders(xhttp, dynizerJwtToken, tenant);

        xhttp.onreadystatechange = () => {
            if (xhttp.readyState == XMLHttpRequest.DONE) {
                if (xhttp.status === http.OK) {
                    let res = xhttp.responseText;
                    resolve(res);
                } else {
                    let error = buildXhttpError(xhttp, url);
                    reject(error);
                }
            }
        };

        xhttp.send();
    });
};

const setXhttpHeaders = (
    xhttp: XMLHttpRequest,
    token: string,
    tenant: string
) => {
    xhttp.setRequestHeader("Authorization", `Bearer ${token}`);
    xhttp.setRequestHeader("Content-Type", "application/octet-stream");
    xhttp.setRequestHeader(DYNIZER_TENANT_HEADER_KEY, tenant);
};

const buildXhttpError = (xhttp: XMLHttpRequest, url: string) => {
    let responseText =
        xhttp.responseType === "text" || xhttp.responseType === ""
            ? xhttp.responseText
            : "";

    return `Request to ${url} failed with status ${xhttp.status}${
        xhttp.statusText ? ", " + xhttp.statusText : ""
    }${responseText ? ": " + responseText : ""}`;
};

const openFileInNewTab = (item: DriveItem) => {
    item?.webUrl && window.open(item.webUrl, "_blank");
};

const useStyles = makeStyles({
    summaryWrapper: {
        marginLeft: "5px",
        marginRight: "5px",
        overflowY: "auto",

        /* overwrite properties of consono-summary webcomponent */
        "& .component-container": {
            ...shorthands.padding("5px !important"),
            ...shorthands.margin("0px !important"),
            color: `${tokens.colorNeutralForeground1} !important`,
            "& .component-detail-header-container": {
                backgroundColor: `${tokens.colorNeutralBackground1Hover} !important`,
                ...shorthands.borderColor(
                    `${tokens.colorNeutralBackground1Hover} !important`
                ),
            },
        },
    },
    paddingTopBottom: {
        backgroundColor: tokens.colorNeutralBackground2,
        paddingBottom: "30px",
        paddingTop: "30px",
        height: "100vh",
    },

    header: {
        backgroundColor: tokens.colorNeutralBackground4,
        display: "flex",
    },
});
